import {
  Anchor,
  Paragraph as BaseParagraph,
  ParagraphProps,
  SizableText,
  Stack,
  YStack,
  XStack,
} from 'tamagui'
import { Fragment } from 'react'
import { Text as TextNode } from 'slate'
import { useState } from 'react'
import { ChevronUp, ChevronDown } from '@tamagui/lucide-icons'
import { H1, H2, H3 } from '../typography'
import { Button, SmartLink, isWeb } from '@my/ui'

interface Node {
  type: string
  children: Node[]
  [key: string]: any
}

interface Serialize {
  children: Node[]
  color: string
  isOrdered?: boolean
  textProps?: ParagraphProps
  asText?: boolean // Render as plain text without paragraph wrapper
}

const getInternalLinkUrl = (node: any) => {
  if (!node.doc) {
    return null
  }

  // We need a slug to generate the link!
  if (!node.doc?.value?.slug) {
    return null
  }

  switch (node.doc.relationTo) {
    case 'articles':
      return `/life/${node.doc.value.slug}`

    case 'brands':
      return `/discounts/${node.doc.value.slug}`

    case 'collections':
      return `/offers/collections/${node.doc.value.slug}`

    case 'offers':
      return `/discounts/${node.doc.value.brand.slug}/${node.doc.value.slug}`

    case 'StaticContentPages':
      return `/${node.doc.value.slug}`

    default:
      return null
  }
}

const serialize = ({ children, color, isOrdered, textProps, asText }: Serialize) => {
  const Paragraph = (props: ParagraphProps) => (
    <BaseParagraph {...props} color="$grey11" {...textProps} size="$4" />
  )

  if (!children) {
    return null
  }

  return children.map((node, i) => {
    if (TextNode.isText(node)) {
      if (asText) return node.text

      let text = <Paragraph key={i}>{node.text}</Paragraph>
      if (node.bold) {
        text = (
          <Paragraph size="$5" fow="500" key={i}>
            {node.text}
          </Paragraph>
        )
      }

      if (node.code) {
        text = <Paragraph key={i}>{node.text}</Paragraph>
      }

      if (node.italic) {
        text = (
          <Paragraph fontStyle="italic" key={i}>
            {node.text}
          </Paragraph>
        )
      }

      if (node.underline) {
        text = (
          <Paragraph textDecorationLine="underline" key={i}>
            {node.text}
          </Paragraph>
        )
      }

      if (node.strikethrough) {
        text = (
          <Paragraph textDecorationLine="line-through" key={i}>
            {node.text}
          </Paragraph>
        )
      }

      return <Fragment key={i}>{text}</Fragment>
    }

    if (!node) {
      return null
    }

    switch (node.type) {
      case 'h1':
      case 'h2':
      case 'h3':
        const levels = {
          h1: H1,
          h2: H2,
          h3: H3,
        }
        const Component = levels[node.type]
        return (
          <Component color="$grey11" fow="500" key={i} {...textProps}>
            {serialize({ children: node.children, color, asText: true })}
          </Component>
        )
      case 'indent':
        return (
          <Stack borderLeftWidth={2} borderLeftColor="$secondary6" pl="$3.5" key={i}>
            {serialize({ children: node.children, color })}
          </Stack>
        )
      case 'center': {
        return (
          <Stack alignItems="center" justifyContent="center" key={i}>
            {serialize({
              children: node.children,
              color,
              textProps: {
                textAlign: 'center',
              },
            })}
          </Stack>
        )
      }
      case 'ul':
        return <YStack tag="ul">{serialize({ children: node.children, color })}</YStack>
      case 'ol':
        return (
          <YStack tag="ol">{serialize({ children: node.children, color, isOrdered: true })}</YStack>
        )
      case 'li':
        return (
          <Paragraph tag="li" key={i} py="$1" pl="$3.5">
            {isOrdered ? `${i + 1}. ` : '• '}
            {serialize({ children: node.children, color, isOrdered })}
          </Paragraph>
        )
      case 'link':
        const url = node.linkType === 'internal' ? getInternalLinkUrl(node) : node.url

        // Prevent creating broken links
        if (!url) {
          return null
        }

        if (node.linkType === 'internal' && !isWeb) {
          return (
            <SmartLink
              Component={Anchor}
              href={url}
              key={i}
              textDecorationLine="underline"
              {...(node.newTab && { target: '_blank' })}
              {...textProps}
            >
              {serialize({
                children: node.children,
                color,
                asText: true,
              })}
            </SmartLink>
          )
        }

        return (
          <Anchor
            href={url}
            key={i}
            textDecorationLine="underline"
            {...(node.newTab && { target: '_blank' })}
            {...textProps}
          >
            {serialize({
              children: node.children,
              color,
              asText: true,
            })}
          </Anchor>
        )

      default:
        return (
          <SizableText key={i} {...textProps}>
            {serialize({ children: node.children, color, textProps })}
          </SizableText>
        )
    }
  })
}

export interface RichTextProps {
  content: any
  color?: string
  expandable?: boolean
  textProps?: ParagraphProps
}

export function RichText({ content, expandable, color = '$primary1', textProps }: RichTextProps) {
  const [showMore, setShowMore] = useState(false)

  if (expandable && content.length > 1) {
    const topContent = content[0]
    const expandableContent = content.slice(1)

    return (
      <YStack space={4}>
        <YStack space={4}>{serialize({ children: [topContent], color, textProps })}</YStack>
        {showMore && (
          <YStack space={4}>{serialize({ children: expandableContent, color, textProps })}</YStack>
        )}
        <XStack jc="center" pt="$2">
          <Button
            plain
            size="$4"
            fontWeight="500"
            iconAfter={showMore ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
            onPress={() => setShowMore(!showMore)}
          >
            {showMore ? 'Show Less' : 'Show More'}
          </Button>
        </XStack>
      </YStack>
    )
  }
  return <YStack gap="$2.5">{serialize({ children: content, color, textProps })}</YStack>
}
